import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Check } from "lucide-react";

export const HeroSection = () => {
  const { elementRef: heroRef, isVisible: heroVisible } = useScrollAnimation();

  return (
    <section 
      ref={heroRef}
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
    >
      {/* Image Background */}
      <img 
        src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1400&q=80"
        alt="Dramatic mountain wilderness landscape"
        width="1400"
        height="900"
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Brand gradient overlay - maintains colors + text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-midnight-green/70 via-deep-teal/60 to-midnight-green/70 z-10" />
      
      {/* Enhanced black gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 z-10" />
      
      {/* Copper accent gradient */}
      <div className="absolute inset-0 bg-gradient-to-tr from-copper/12 via-transparent to-transparent z-10" />
      
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 z-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>
      
      {/* Decorative animated elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-copper/15 rounded-full blur-3xl animate-pulse-slow z-5" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-deep-teal/20 rounded-full blur-3xl animate-pulse-slow z-5" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="relative z-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div 
          className={`transition-all duration-1000 ease-out ${
            heroVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-12'
          }`}
        >
          {/* Headline */}
          <h1 className="font-playfair text-5xl md:text-7xl font-bold text-ivory mb-6 leading-tight">
            Built for Camp Kitchens,<br />
            Powered by Camp People
          </h1>
          
          {/* Subheadline */}
          <p className="font-inter text-xl md:text-2xl text-ivory/90 mb-8 max-w-3xl mx-auto">
            Plan menus, manage inventory, and order from Sysco — all in one simple system built for camps, retreats, and outdoor programs.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg" 
              asChild
              className="bg-copper hover:bg-copper/90 text-ivory font-semibold text-lg px-8 py-6 h-auto shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Link to="/contact">Request a Camp Demo</Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              asChild
              className="border-2 border-ivory/30 bg-white/10 backdrop-blur-sm text-ivory hover:bg-white/20 font-semibold text-lg px-8 py-6 h-auto"
            >
              <a href="/#features">Watch How It Works</a>
            </Button>
          </div>

          {/* Trust bar */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-ivory/80 text-sm">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-copper" />
              <span>Connected with Sysco</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-copper" />
              <span>Trusted by Camp Directors Nationwide</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-copper" />
              <span>Saves 40+ Hours Monthly</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-sand to-transparent z-10" />
    </section>
  );
};
